/** Lightweight SVG charts (no chart library — keeps the bundle lean). */

export function BarChart({ labels, values, unit }: { labels: string[]; values: number[]; unit: string }): JSX.Element {
  const W = 860;
  const H = 190;
  const max = Math.max(...values, 1);
  const bw = (W - 40) / values.length;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", display: "block" }}>
      <text x={4} y={12} fontSize={10} fill="var(--chart-axis)">{unit}</text>
      {values.map((v, i) => {
        const h = ((H - 46) * v) / max;
        return (
          <g key={i}>
            <rect x={24 + i * bw + bw * 0.14} y={H - 26 - h} width={bw * 0.72} height={h} rx={3} fill="var(--chart-bar)" stroke="var(--chart-bar-stroke)" />
            <text x={24 + i * bw + bw / 2} y={H - 12} fontSize={10} fill="var(--chart-axis)" textAnchor="middle">{labels[i]}</text>
            {v > 0 && <text x={24 + i * bw + bw / 2} y={H - 31 - h} fontSize={9.5} fill="var(--chart-axis)" textAnchor="middle">{Math.round(v).toLocaleString("de-DE")}</text>}
          </g>
        );
      })}
    </svg>
  );
}

export function CashflowChart({ cashflow, investment }: { cashflow: { year: number; cumulativeEur: number }[]; investment: number }): JSX.Element {
  const W = 860;
  const H = 220;
  const vals = [-investment, ...cashflow.map((c) => c.cumulativeEur)];
  const mn = Math.min(...vals);
  const mx = Math.max(...vals, 1);
  const X = (i: number) => 34 + ((W - 54) * i) / (vals.length - 1);
  const Y = (v: number) => 14 + (H - 46) * (1 - (v - mn) / (mx - mn || 1));
  const be = vals.findIndex((v) => v >= 0);
  const path = vals.map((v, i) => `${i === 0 ? "M" : "L"}${X(i).toFixed(1)},${Y(v).toFixed(1)}`).join(" ");
  const area = `${path} L${X(vals.length - 1).toFixed(1)},${Y(Math.min(mn, 0)).toFixed(1)} L${X(0).toFixed(1)},${Y(Math.min(mn, 0)).toFixed(1)} Z`;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", display: "block" }}>
      <line x1={34} y1={Y(0)} x2={W - 20} y2={Y(0)} stroke="var(--chart-grid)" strokeDasharray="4 4" />
      <text x={4} y={Y(0) + 3} fontSize={10} fill="var(--chart-axis)">0 €</text>
      <path d={area} fill="var(--chart-area)" />
      <path d={path} fill="none" stroke="var(--chart-line)" strokeWidth={2.5} />
      {be > 0 && (
        <g>
          <circle cx={X(be)} cy={Y(vals[be]!)} r={5} fill="var(--chart-positive)" />
          <text x={X(be) - 30} y={Y(vals[be]!) - 10} fontSize={11} fontWeight={700} fill="var(--chart-positive)">Break-even: year {be}</text>
        </g>
      )}
      {[0, 5, 10, 15, 20, 25].map((y) => (
        <text key={y} x={X(Math.min(y, vals.length - 1)) - 3} y={H - 6} fontSize={10} fill="var(--chart-axis)">{y}</text>
      ))}
      <text x={4} y={18} fontSize={10} fill="var(--chart-axis)">{Math.round(mx / 1000)}k€</text>
      <text x={4} y={H - 30} fontSize={10} fill="var(--chart-axis)">{Math.round(mn / 1000)}k€</text>
    </svg>
  );
}
